/**
 * Reducir en el navegador una imagen que pasa del máximo por fichero del plan, antes de
 * subirla (CLAUDE.md §8). Una captura de móvil de 24 MB no cabe en los 10 MB de un plan,
 * y el núcleo la rechaza, con razón: el límite es suyo y mide lo que le llega. Lo que
 * falta es no hacérsela subir entera para nada. Aquí, y no en el servidor: la subida de
 * 25 MB cruza igual la conexión del cliente, decodificarla la paga nuestro único servidor,
 * y reducir allí antes de medir dejaría el límite del plan midiendo nuestra salida.
 *
 * Solo toca lo que **no cabría**: lo que hoy entra, entra igual. Nunca un GIF, un WebP
 * animado ni un APNG (el lienzo se come la animación: saldría una foto fija sin avisar), ni
 * un SVG. Si no lo consigue —no puede decodificarla o ni la más pequeña cabe—, se sube el
 * original y el núcleo dice lo suyo.
 *
 * De una en una: una captura de móvil decodificada ocupa unos 100 MB más su lienzo, y el
 * alta sube tres imágenes a la vez; tres reducciones a la vez pueden cerrar la pestaña de
 * un móvil a mitad del alta. Las subidas siguen en paralelo; reducir, no.
 */

/** Ancho máximo del resultado: más que cualquier pantalla en la que se vaya a ver. */
export const ANCHO_MAXIMO = 2560

/**
 * Píxeles como mucho. Por área y no por el lado mayor: una captura de 1857×13452 con
 * «2560 de lado mayor» quedaría en una tira de 353 px de ancho, ilegible; así queda en
 * unos 1486×10766, por debajo del tope de lienzo de Safari en iOS (16 777 216 px).
 */
export const PIXELES_MAXIMOS = 16_000_000

/** Los intentos, de menos a más agresivo: factor sobre las medidas y calidad. */
export const INTENTOS: ReadonlyArray<{ calidad: number; factor: number }> = [
  { factor: 1, calidad: 0.85 },
  { factor: 1, calidad: 0.72 },
  { factor: 0.75, calidad: 0.72 },
]

/**
 * El lado más largo que Chrome sabe codificar en WebP: por encima, `toBlob` devuelve
 * `null`. Una captura larga de Android (1080×20000) limitada solo por área quedaría en
 * 929×17215 y no saldría nunca en WebP.
 */
export const LADO_MAXIMO = 16_383

export const medidasReducidas = (ancho: number, alto: number, factor = 1): { alto: number; ancho: number } => {
  const escala = Math.min(1, ANCHO_MAXIMO / ancho, LADO_MAXIMO / ancho, LADO_MAXIMO / alto, Math.sqrt(PIXELES_MAXIMOS / (ancho * alto))) * factor
  // Hacia abajo, para no pasar nunca de los topes por un redondeo.
  return { ancho: Math.max(1, Math.floor(ancho * escala + 1e-9)), alto: Math.max(1, Math.floor(alto * escala + 1e-9)) }
}

/** ¿Se puede reducir en un lienzo sin perder nada que importe? */
const esReducible = (fichero: File): boolean => {
  const tipo = fichero.type.toLowerCase()
  if (tipo === 'image/gif' || tipo === 'image/svg+xml' || /\.(gif|svg)$/i.test(fichero.name)) return false
  return tipo.startsWith('image/') || (tipo === '' && /\.(jpe?g|png|webp|avif|bmp)$/i.test(fichero.name))
}

/** ¿Hay que intentar reducirla? Solo si pasa del máximo, y sin máximo conocido nunca. */
export const hayQueReducir = (fichero: File, maximoBytes: null | number | undefined): boolean =>
  typeof maximoBytes === 'number' && maximoBytes > 0 && fichero.size > maximoBytes && esReducible(fichero)

const bytesDe = async (fichero: Blob, desde: number, hasta: number): Promise<Uint8Array> => new Uint8Array(await fichero.slice(desde, hasta).arrayBuffer())
const letras = (bytes: Uint8Array, desde: number, largo: number): string => String.fromCharCode(...bytes.subarray(desde, desde + largo))

/**
 * ¿Es una imagen animada que el lienzo dejaría en su primer fotograma? Se mira la cabecera,
 * sin decodificar nada:
 *
 * - un WebP extendido (`VP8X`) con el bit de animación en sus banderas;
 * - un PNG con `acTL` antes del primer `IDAT`, que es lo que hace de él un APNG.
 *
 * Lo que no se sepa leer se da por fija: lo peor que pasa es lo de antes.
 */
export const esAnimada = async (fichero: Blob): Promise<boolean> => {
  try {
    const cabecera = await bytesDe(fichero, 0, 32)
    if (letras(cabecera, 0, 4) === 'RIFF' && letras(cabecera, 8, 4) === 'WEBP') {
      return letras(cabecera, 12, 4) === 'VP8X' && ((cabecera[20] ?? 0) & 0x02) !== 0
    }
    if (cabecera[0] === 0x89 && letras(cabecera, 1, 3) === 'PNG') {
      let posicion = 8
      // Los trozos de antes de la imagen son pocos (IHDR, el perfil de color, textos…).
      for (let i = 0; i < 64 && posicion + 8 <= fichero.size; i++) {
        const trozo = await bytesDe(fichero, posicion, posicion + 8)
        const tipo = letras(trozo, 4, 4)
        if (tipo === 'acTL') return true
        if (tipo === 'IDAT' || tipo === 'IEND') return false
        const largo = ((trozo[0]! << 24) | (trozo[1]! << 16) | (trozo[2]! << 8) | trozo[3]!) >>> 0
        posicion += 12 + largo
      }
    }
    return false
  } catch {
    return false
  }
}

// La fila de reducciones: de una en una en toda la pestaña (ver arriba).
let turno: Promise<unknown> = Promise.resolve()
const enFila = <T>(hacer: () => Promise<T>): Promise<T> => {
  const resultado = turno.then(hacer, hacer)
  turno = resultado.catch(() => undefined)
  return resultado
}

type Formato = 'image/jpeg' | 'image/webp'

/** Una imagen decodificada, lista para dibujarla a otras medidas. */
export type Decodificada = { alto: number; ancho: number; cerrar: () => void; fuente: unknown }

/** Lo que hace falta del navegador: separado para poder probarlo sin él. */
export type EntornoDeImagen = {
  decodificar: (fichero: File) => Promise<Decodificada>
  /** Dibuja a esas medidas y codifica; con JPEG, sobre blanco (no tiene transparencia). */
  codificar: (imagen: Decodificada, medidas: { alto: number; ancho: number }, formato: Formato, calidad: number) => Promise<Blob | null>
}

export const entornoDelNavegador: EntornoDeImagen = {
  decodificar: async (fichero) => {
    // `createImageBitmap` respeta la orientación EXIF, y de paso se quedan fuera los
    // metadatos (el GPS de una foto de móvil incluido).
    const mapa = await createImageBitmap(fichero)
    return { ancho: mapa.width, alto: mapa.height, fuente: mapa, cerrar: () => mapa.close() }
  },
  codificar: (imagen, { ancho, alto }, formato, calidad) =>
    new Promise((resolver) => {
      const lienzo = document.createElement('canvas')
      lienzo.width = ancho
      lienzo.height = alto
      const contexto = lienzo.getContext('2d')
      if (!contexto) return resolver(null)
      if (formato === 'image/jpeg') {
        contexto.fillStyle = '#ffffff'
        contexto.fillRect(0, 0, ancho, alto)
      }
      contexto.imageSmoothingQuality = 'high'
      contexto.drawImage(imagen.fuente as ImageBitmap, 0, 0, ancho, alto)
      lienzo.toBlob(
        (blob) => {
          // Safari no suelta la memoria de un lienzo grande hasta que se le quita el tamaño.
          lienzo.width = 0
          lienzo.height = 0
          resolver(blob)
        },
        formato,
        calidad,
      )
    }),
}

const EXTENSION: Record<Formato, string> = { 'image/jpeg': '.jpg', 'image/webp': '.webp' }

/**
 * El fichero listo para subir: el mismo si cabe (o no se puede reducir), o uno nuevo en
 * WebP —JPEG si el navegador no sabe codificar WebP— que quepa en `maximoBytes`.
 *
 * `alEmpezar` avisa cuando de verdad empieza a reducirla —no para una animada, y no mientras
 * espera su turno detrás de otra—, para que la pantalla diga «Reduciendo…» cuando es verdad.
 */
export const reducirSiHaceFalta = async (
  fichero: File,
  maximoBytes: null | number | undefined,
  entorno: EntornoDeImagen = entornoDelNavegador,
  alEmpezar?: () => void,
): Promise<File> => {
  if (!hayQueReducir(fichero, maximoBytes)) return fichero
  if (await esAnimada(fichero)) return fichero
  return enFila(() => {
    alEmpezar?.()
    return reducir(fichero, maximoBytes as number, entorno)
  })
}

const reducir = async (fichero: File, maximoBytes: number, entorno: EntornoDeImagen): Promise<File> => {
  let imagen: Decodificada
  try {
    imagen = await entorno.decodificar(fichero)
  } catch {
    return fichero
  }
  try {
    let formato: Formato = 'image/webp'
    for (const { factor, calidad } of INTENTOS) {
      const medidas = medidasReducidas(imagen.ancho, imagen.alto, factor)
      let blob = await entorno.codificar(imagen, medidas, formato, calidad)
      // Un navegador que no codifica WebP devuelve PNG sin decir nada, o nada (`null`) si
      // la imagen le parece demasiado grande para WebP: a JPEG desde aquí, a las mismas medidas.
      if (formato === 'image/webp' && (!blob || blob.type !== 'image/webp')) {
        formato = 'image/jpeg'
        blob = await entorno.codificar(imagen, medidas, formato, calidad)
      }
      if (blob && blob.size > 0 && blob.size <= maximoBytes) {
        const base = fichero.name.replace(/\.[^.]+$/, '') || 'imagen'
        return new File([blob], `${base}${EXTENSION[formato]}`, { type: formato, lastModified: fichero.lastModified })
      }
    }
    return fichero
  } catch {
    return fichero
  } finally {
    imagen.cerrar()
  }
}
